import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card } from '@bruno/ui';
import { deleteWebsite, getWebsite, rotateEmbedKey } from '../api/websites';
import { KnowledgeFilesPanel } from '../components/KnowledgeFilesPanel';
import { SubscriptionPanel } from '../components/SubscriptionPanel';
import { WidgetCustomizationPanel } from '../components/WidgetCustomizationPanel';

export function WebsiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['websites', id],
    queryFn: () => getWebsite(id!),
    enabled: !!id,
  });

  const rotate = useMutation({
    mutationFn: () => rotateEmbedKey(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['websites', id] }),
  });

  const remove = useMutation({
    mutationFn: () => deleteWebsite(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['websites'] });
      navigate('/websites');
    },
  });

  if (isLoading || !data) return <div className="text-slate-500">Loading...</div>;

  function copy() {
    if (!data) return;
    setCopyError(null);
    if (!navigator.clipboard?.writeText) {
      setCopyError('Clipboard is not available. Select the snippet above and copy it manually.');
      return;
    }
    void navigator.clipboard
      .writeText(data.snippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setCopyError('Clipboard permission was denied. Select the snippet above and copy it manually.');
      });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/websites" className="text-sm text-slate-500 hover:text-slate-700">
          ← Back to websites
        </Link>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{data.name}</h1>
          <div className="text-slate-500 text-sm">{data.domain}</div>
        </div>
        <Badge tone={data.status === 'active' ? 'green' : data.status === 'paused' ? 'amber' : 'red'}>
          {data.status}
        </Badge>
      </div>

      <Card>
        <h2 className="font-semibold mb-2">Install Bruno</h2>
        <p className="text-sm text-slate-500 mb-4">
          Paste this snippet into the <code className="text-slate-700">&lt;head&gt;</code> of your website.
        </p>
        <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-sm break-all">
          {data.snippet}
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" size="sm" onClick={copy}>
            {copied ? 'Copied!' : 'Copy snippet'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => rotate.mutate()}
            loading={rotate.isPending}
          >
            Rotate embed key
          </Button>
        </div>
        {copyError ? <p className="text-xs text-red-600 mt-2">{copyError}</p> : null}
        <p className="text-xs text-slate-500 mt-3">
          Embed key: <code>{data.embedKey}</code>
        </p>
      </Card>

      <Card>
        <h2 className="font-semibold mb-2">Knowledge base</h2>
        <KnowledgeFilesPanel websiteId={data.id} />
      </Card>

      <Card>
        <h2 className="font-semibold mb-3">Widget customization</h2>
        <WidgetCustomizationPanel website={data} />
      </Card>

      <Card>
        <h2 className="font-semibold mb-3">Subscription</h2>
        <SubscriptionPanel websiteId={data.id} />
      </Card>

      <Card className="border-red-200 bg-red-50/50">
        <h2 className="font-semibold mb-2 text-red-800">Danger zone</h2>
        <p className="text-sm text-red-700/80 mb-3">
          Deleting this website removes all associated knowledge files and conversations.
        </p>
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            if (confirm('Delete this website? This cannot be undone.')) {
              remove.mutate();
            }
          }}
        >
          Delete website
        </Button>
      </Card>
    </div>
  );
}
